import React, { useCallback, useEffect, useState } from "react";
import { PhxButton } from "@/design-system";
import { formatApiError } from "@/lib/api";
import { portalApi, portalToken } from "./portalApi";

function Login({ onDone }) {
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await portalApi.post("/login", { login_id: loginId, password });
      portalToken.set(r.data.token);
      onDone(r.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-sm mx-auto mt-16 p-6 border bg-white space-y-3" data-testid="portal-login">
      <h1 className="text-lg font-semibold">Customer login</h1>
      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
      <label className="block text-sm">Login ID
        <input className="block w-full border p-2" value={loginId} onChange={(e) => setLoginId(e.target.value)} />
      </label>
      <label className="block text-sm">Password
        <input type="password" className="block w-full border p-2" value={password}
          onChange={(e) => setPassword(e.target.value)} />
      </label>
      <PhxButton type="submit" disabled={busy || !loginId || !password}>Sign in</PhxButton>
    </form>
  );
}

function Orders({ party }) {
  const [catalogue, setCatalogue] = useState([]);
  const [orders, setOrders] = useState([]);
  const [qty, setQty] = useState({});
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, o] = await Promise.all([portalApi.get("/catalogue"), portalApi.get("/orders")]);
      setCatalogue(c.data.items || []);
      setOrders(o.data.items || []);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const lines = catalogue
    .filter((p) => Number(qty[p.id]) > 0)
    .map((p) => ({ product_id: p.id, product_name: p.name, sku: p.sku, ordered_qty: Number(qty[p.id]) }));

  const place = async () => {
    setBusy(true);
    try {
      await portalApi.post("/orders", { party_id: party, lines });
      setQty({});
      setMsg("Order sent. It will be processed once our team approves it.");
      await load();
    } catch (e) {
      setMsg(null);
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="portal-orders">
      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
      {msg && <p className="text-sm text-green-700" data-testid="portal-msg">{msg}</p>}
      <section>
        <h2 className="text-sm font-semibold uppercase mb-2">New order</h2>
        <ul className="divide-y border bg-white">
          {catalogue.map((p) => (
            <li key={p.id} className="flex items-center justify-between p-2 text-sm">
              <span>{p.name} <span className="text-slate-500">({p.sku}, {p.uom})</span></span>
              <input aria-label={`qty-${p.sku}`} type="number" min="0" className="w-24 border p-1"
                value={qty[p.id] || ""} onChange={(e) => setQty({ ...qty, [p.id]: e.target.value })} />
            </li>
          ))}
        </ul>
        <PhxButton className="mt-3" disabled={busy || lines.length === 0} onClick={place}>
          Place order ({lines.length} items)
        </PhxButton>
      </section>
      <section>
        <h2 className="text-sm font-semibold uppercase mb-2">My orders</h2>
        <ul className="divide-y border bg-white">
          {orders.map((o) => (
            <li key={o.id} className="flex justify-between p-2 text-sm" data-testid={`my-order-${o.id}`}>
              <span>{o.number || o.id}</span><span>{o.state}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function Requests() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ type: "statement", format: "pdf", date_from: "", date_to: "", document_id: "", order_id: "" });
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await portalApi.get("/requests");
      setItems(r.data.items || []);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    const body = { type: form.type, format: form.format };
    if (form.type === "statement") { body.date_from = form.date_from; body.date_to = form.date_to; }
    if (form.type === "invoice") body.document_id = form.document_id;
    if (form.type === "order_copy") body.order_id = form.order_id;
    try {
      await portalApi.post("/requests", body);
      setError(null);
      await load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const download = async (r) => {
    try {
      const res = await portalApi.get(`/requests/${r.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${r.type}.${r.format === "jpeg" ? "jpg" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="space-y-4" data-testid="portal-requests">
      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
      <div className="border bg-white p-3 space-y-2 text-sm">
        <select aria-label="type" value={form.type} onChange={set("type")} className="border p-1">
          <option value="statement">Statement</option>
          <option value="invoice">Invoice</option>
          <option value="order_copy">Order copy</option>
        </select>{" "}
        <select aria-label="format" value={form.format} onChange={set("format")} className="border p-1">
          <option value="pdf">PDF</option>
          <option value="jpeg">JPEG</option>
        </select>
        {form.type === "statement" && (
          <div className="flex gap-2">
            <input aria-label="from" type="date" value={form.date_from} onChange={set("date_from")} className="border p-1" />
            <input aria-label="to" type="date" value={form.date_to} onChange={set("date_to")} className="border p-1" />
          </div>
        )}
        {form.type === "invoice" && (
          <input aria-label="invoice id" placeholder="Invoice ID" value={form.document_id} onChange={set("document_id")} className="border p-1 block" />
        )}
        {form.type === "order_copy" && (
          <input aria-label="order id" placeholder="Order ID" value={form.order_id} onChange={set("order_id")} className="border p-1 block" />
        )}
        <PhxButton onClick={send}>Request</PhxButton>
      </div>
      <ul className="divide-y border bg-white">
        {items.map((r) => (
          <li key={r.id} className="flex justify-between items-center p-2 text-sm" data-testid={`my-request-${r.id}`}>
            <span>{String(r.type).replace("_", " ")} ({String(r.format).toUpperCase()}) — {r.state}
              {r.decision_note ? ` — ${r.decision_note}` : ""}</span>
            {r.state === "APPROVED" && <PhxButton onClick={() => download(r)}>Download</PhxButton>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function PortalApp() {
  const [session, setSession] = useState(portalToken.get() ? { party_id: null } : null);
  const [tab, setTab] = useState("orders");

  useEffect(() => {
    const out = () => setSession(null);
    window.addEventListener("portal-logout", out);
    return () => window.removeEventListener("portal-logout", out);
  }, []);

  useEffect(() => {
    if (session && !session.party_id) {
      portalApi.get("/me").then((r) => setSession({ party_id: r.data.party_id })).catch(() => {});
    }
  }, [session]);

  if (!session) return <Login onDone={(s) => setSession({ party_id: s.party_id })} />;

  return (
    <div className="max-w-3xl mx-auto p-4" data-testid="portal-app">
      <header className="flex justify-between items-center mb-4">
        <nav className="flex gap-2">
          <PhxButton variant={tab === "orders" ? "primary" : "secondary"} onClick={() => setTab("orders")}>Orders</PhxButton>
          <PhxButton variant={tab === "requests" ? "primary" : "secondary"} onClick={() => setTab("requests")}>Documents</PhxButton>
        </nav>
        <PhxButton variant="secondary" onClick={() => { portalToken.clear(); setSession(null); }}>Sign out</PhxButton>
      </header>
      {tab === "orders" ? (session.party_id ? <Orders party={session.party_id} /> : null) : <Requests />}
    </div>
  );
}
